import * as React from 'react';
import { connect } from 'react-redux';
import { get, isEmpty, isNil } from 'lodash';

import ActionButton from 'components/buttons/action-button/ActionButton';
import { ModalFrame, ModalFrameFooter, ModalFrameInner, ModalFrameMessage } from 'components/modal';
import SubcampaignsMultiSelect from '../subcampaigns-multi-select/SubcampaignsMultiSelect';

import { PromptExit, navigate } from 'libs/router';

import {
  fetchShortlistedSubcampaigns,
  resetBulkOffer,
  saveCurrentSelectedSubcampaigns
} from '../../../reducers/bulkOffer';
import { fetchOfferDeliverables } from '../../../reducers/offerDeliverables';
import { copyOfferDetailsWithDeliverables } from 'modules/subcampaigns/reducers/offerDetailsBuffer';
import { isEnableEntertainmentVideoSelector } from 'selectors/enableEntertainmentVideo';

import { __t, applyTranslations } from 'utils/language';
import { addThousandsSeparator } from 'utils/helper/common';
import { canInfluencerProvideSuchDeliverables } from 'utils/deliverables';

import {
  BULK_OFFER_MODAL_MODE_CREATE,
  BULK_OFFER_MODAL_MODE_UPDATE,
  BULK_TYPE_CREATE_OFFERS,
  MIN_SUBCAMPAIGNS_COUNT_FOR_BULK_CREATION,
  getBulkOfferSelectedSubcampaigns,
  sumBulkOfferBudgets
} from 'utils/bulk-offer';

import type {
  Campaign,
  CampaignOverview,
  Deliverable,
  Subcampaign,
  SubcampaignBulkOfferStore,
  SubcampaignCreateBulkOffer,
  SubcampaignCreateBulkOfferMode,
  SubcampaignOffer,
  SubcampaignSelectedIdsMap
} from 'types';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('./bulk-offer-modal.i18n.json'));

type Handler = () => void;

type OwnProps = {
  campaignId: number,
  subcampaign?: Subcampaign,
  preselectedSubcampaignId?: number,
  sentSubcampaignId?: number,
  mode: SubcampaignCreateBulkOfferMode,
  bulkOffer?: SubcampaignCreateBulkOffer,
  onCloseModal: Handler,
  isNewCampaign?: boolean // Will be used when new task card are rolled out.
};

type StoreProps = {
  bulkOfferStore: SubcampaignBulkOfferStore,
  bulkOfferDeliverables: Array<Deliverable> | undefined | null,
  campaignOverview: CampaignOverview | undefined | null,
  campaignBrief: Campaign | undefined | null,
  isEntertainmentVideoEnabled: boolean
};

type DispatchProps = {
  dispatchFetchOfferDeliverables: (a: number) => Promise<void>,
  dispatchCopyOffer: (offer: SubcampaignOffer) => Promise<void>,
  dispatchFetchShortlistSubcampaigns: (a: number) => Promise<void>,
  dispatchSaveCurrentSelectedSubcampaigns: (a: SubcampaignSelectedIdsMap) => Promise<void>,
  dispatchResetBulkOffer: () => Promise<void>
};

type Props = OwnProps & StoreProps & DispatchProps;

type State = {
  isModified: boolean,
  selectedIdsMap: SubcampaignSelectedIdsMap
};

export class BulkOfferModal extends React.PureComponent<Props, State> {
  state = {
    selectedIdsMap: {
      ...this.props.bulkOfferStore.selectedIdsMap,
      ...(this.props.preselectedSubcampaignId ? { [this.props.preselectedSubcampaignId]: true } : {})
    },
    isModified: Boolean(this.props.preselectedSubcampaignId)
  };

  componentDidMount() {
    const { bulkOffer, campaignId, mode, dispatchFetchOfferDeliverables, dispatchFetchShortlistSubcampaigns, dispatchResetBulkOffer } = this.props;

    if (mode === BULK_OFFER_MODAL_MODE_CREATE) {
      dispatchResetBulkOffer();
    }

    if (bulkOffer && bulkOffer.shouldLoadDeliverables) {
      dispatchFetchOfferDeliverables(bulkOffer.offer.id);
    }

    dispatchFetchShortlistSubcampaigns(campaignId);
  }

  calcRemainingBudget(): number {
    const { bulkOfferStore, campaignOverview, bulkOffer } = this.props;
    const { selectedIdsMap } = this.state;

    const remainingBudgetBeforeBulkOffer = get(campaignOverview, 'remainingBudget') || 0;
    const bulkSum = sumBulkOfferBudgets(
      getBulkOfferSelectedSubcampaigns(bulkOfferStore, selectedIdsMap),
      bulkOfferStore.customizedFieldsByIdMap,
      get(bulkOffer, 'offer.budget')
    );

    return remainingBudgetBeforeBulkOffer - bulkSum;
  }

  getDisabledIdsMap = () => {
    const { bulkOfferStore, bulkOfferDeliverables, isNewCampaign, isEntertainmentVideoEnabled } = this.props;
    const { shortlistedSubcampaigns } = bulkOfferStore;

    return shortlistedSubcampaigns.reduce((map, s) => {
      map[s.id] = !canInfluencerProvideSuchDeliverables(s.creatorProfile, bulkOfferDeliverables || [], isEntertainmentVideoEnabled, isNewCampaign); // eslint-disable-line no-param-reassign
      return map;
    }, {});
  };

  handleToggle = (subcampaignId: number): void => {
    this.setState(state => ({
      ...state,
      selectedIdsMap: {
        ...state.selectedIdsMap,
        [subcampaignId]: !state.selectedIdsMap[subcampaignId]
      },
      isModified: true
    }));
  };

  handleToggleAll = (subcampaignIds: Array<number>, isSelected: boolean): void => {
    const selectedIdsMap = {};
    const disabledIdsMap = this.getDisabledIdsMap();

    subcampaignIds.forEach(id => {
      if (!disabledIdsMap[id]) {
        selectedIdsMap[id] = isSelected;
      }
    });

    this.setState(state => ({
      ...state,
      selectedIdsMap,
      isModified: true
    }));
  };

  handleSubmit = async () => {
    const {
      onCloseModal,
      mode,
      bulkOfferStore,
      campaignBrief,
      sentSubcampaignId,
      preselectedSubcampaignId,
      subcampaign,
      dispatchCopyOffer,
      dispatchSaveCurrentSelectedSubcampaigns
    } = this.props;
    const { selectedIdsMap } = this.state;

    if (isNil(campaignBrief) || isEmpty(campaignBrief)) {
      return;
    }

    const selectedSubcampaigns = getBulkOfferSelectedSubcampaigns(bulkOfferStore, selectedIdsMap);

    if (selectedSubcampaigns.length < MIN_SUBCAMPAIGNS_COUNT_FOR_BULK_CREATION) {
      onCloseModal();
      return;
    }

    if (mode === BULK_OFFER_MODAL_MODE_CREATE) {
      const baseSubcampaignId = sentSubcampaignId || preselectedSubcampaignId || selectedSubcampaigns[0].id;

      if (subcampaign) {
        const offer = subcampaign.updatedOffer || subcampaign.offer;
        await dispatchCopyOffer(offer);
        dispatchSaveCurrentSelectedSubcampaigns(selectedIdsMap);
        navigate.to(`/campaigns/${campaignBrief.id}/subcampaigns/${baseSubcampaignId}/edit?new=1&bulk=${BULK_TYPE_CREATE_OFFERS}&paste=active`);
      }
      onCloseModal();
      return;
    }

    if (mode === BULK_OFFER_MODAL_MODE_UPDATE) {
      dispatchSaveCurrentSelectedSubcampaigns(selectedIdsMap);
      onCloseModal();
    }
  };

  render() {
    const { bulkOfferStore, campaignBrief, onCloseModal } = this.props;
    const { isLoading, shortlistedSubcampaigns } = bulkOfferStore;
    const { selectedIdsMap, isModified } = this.state;

    const plannedBudget = get(campaignBrief, 'budget');

    const disabledIdsMap = this.getDisabledIdsMap();

    return (
      <ModalFrame
        title={__t('bulk_offer_modal.title')}
        subtitle={!isNil(campaignBrief) && !isEmpty(campaignBrief) ? `"${campaignBrief.name}"` : ''}
      >
        {
          isModified &&
            <PromptExit type="modal" />
        }
        {
          !isNil(plannedBudget) && plannedBudget >= 0 &&
            <ModalFrameMessage
              message={__t('bulk_offer_modal.campaign_desc', {
                totalBudget: addThousandsSeparator(plannedBudget),
                remainingBudget: addThousandsSeparator(this.calcRemainingBudget())
              })}
            />
        }
        <ModalFrameInner>
          <SubcampaignsMultiSelect
            isLoading={isLoading}
            subcampaigns={shortlistedSubcampaigns}
            selectedIdsMap={selectedIdsMap}
            disabledIdsMap={disabledIdsMap}
            titleAllWithSelectAll
            titleAll={__t('bulk_offer_modal.column_title_to_select_from')}
            titleSelected={__t('bulk_offer_modal.column_title_selected')}
            messageAllEmpty={__t('bulk_offer_modal.message_empty_all')}
            messageSelectedEmpty={__t('bulk_offer_modal.message_empty_selected')}
            onToggle={this.handleToggle}
            onToggleAll={this.handleToggleAll}
          />
        </ModalFrameInner>
        <ModalFrameFooter>
          <ActionButton
            color="tertiary"
            onClick={onCloseModal}
          >
            {__t('collabary_common.action_button.cancel')}
          </ActionButton>
          <ActionButton
            color="secondary"
            disabled={isLoading || getBulkOfferSelectedSubcampaigns(bulkOfferStore, selectedIdsMap).length < MIN_SUBCAMPAIGNS_COUNT_FOR_BULK_CREATION}
            onClick={this.handleSubmit}
          >
            {__t('collabary_common.action_button.continue')}
          </ActionButton>
        </ModalFrameFooter>
      </ModalFrame>
    );
  }
}

const mapStateToProps = (
  { account, bulkOffer, campaignOverview, campaignBrief, offerDeliverables, runtimeConfig },
  ownProps: OwnProps
): StoreProps => {
  let deliverables = null;

  if (ownProps.bulkOffer) {
    deliverables = ownProps.bulkOffer.shouldLoadDeliverables
      ? offerDeliverables.deliverablesByOfferId[ownProps.bulkOffer.offer.id]
      : ownProps.bulkOffer.deliverables;
  }

  return {
    bulkOfferStore: bulkOffer,
    bulkOfferDeliverables: deliverables || null,
    campaignOverview: campaignOverview.overview,
    campaignBrief: campaignBrief.brief,
    isEntertainmentVideoEnabled: isEnableEntertainmentVideoSelector({ runtimeConfig, account })
  };
};

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchOfferDeliverables: (offerId: number) => dispatch(fetchOfferDeliverables(offerId, false)),
  dispatchFetchShortlistSubcampaigns: (campaignId: number) => dispatch(fetchShortlistedSubcampaigns(campaignId)),
  dispatchSaveCurrentSelectedSubcampaigns: (selectedIdsMap: SubcampaignSelectedIdsMap) => dispatch(saveCurrentSelectedSubcampaigns(selectedIdsMap)),
  dispatchCopyOffer: (offer: SubcampaignOffer) => dispatch(copyOfferDetailsWithDeliverables(offer)),
  dispatchResetBulkOffer: () => dispatch(resetBulkOffer())
});

export default connect(mapStateToProps, mapDispatchToProps)(BulkOfferModal);
